<template>
  <IconWrapper>
    <TaskIcon v-if="objectiveIsJiraTask(objective)" />
    <WorkItemIcon v-else-if="isWorkItem(objective)" />
    <AppIcon
      v-else-if="objective.customIcon"
      :height="absoluteSize"
      :icon-name="objective.customIcon"
      :width="absoluteSize"
    />
    <OkrElementIcon v-else />
    <div
      v-if="objective.private && withLockIcon"
      v-tippy="{
        content: $t('workspaces.private'),
        placement: 'top',
        theme: DROP_LIST_THEMES.TRANSLUCENT_NEXT
      }"
      class="oi-LockIcon"
    >
      <AppIcon height="9" icon-name="lock-outline" width="9" />
    </div>
  </IconWrapper>
</template>

<script setup>
import { h, computed, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'

import { resolveWorkItemColor } from '@/utils/azure-devops-helpers/work-item'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { SIZES } from '@/utils/components-configurations/okr-icon'
import { memoizeOkrIconStyles } from '@/utils/memoizations'
import { isWorkItem, objectiveIsJiraTask } from '@/utils/objective-types'
import { includesValue } from '@/utils/prop-validators'
import { getScopeId } from '@/utils/render-function-helpers'

import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'OkrIcon'
})
const props = defineProps({
  objective: {
    type: Object,
    required: true
  },

  size: {
    type: String,
    validator: v => includesValue(v, SIZES),
    default: SIZES.MD
  },

  filteredOut: {
    type: Boolean
  },

  withFill: {
    type: Boolean,
    default: true
  },

  withLockIcon: {
    type: Boolean,
    default: true
  }
})

const store = useStore()

const workItemsColorsMap = computed(() => {
  if (!store) return {}

  return store.getters['azureDevops/workItemsColorsMap']
})

const styles = computed(() => {
  const { withFill, objective } = props

  let levelColor = objective.levelColor

  if (isWorkItem(objective) && (objective.issueType || objective.platformElementType)) {
    levelColor = resolveWorkItemColor(objective, workItemsColorsMap.value)
  }

  return memoizeOkrIconStyles(absoluteSize.value, levelColor, withFill)
})

const absoluteSize = computed(() => {
  const { size } = props
  const { MD, LG, SM, XS, XL } = SIZES
  const sizes = {
    [XL]: 48,
    [LG]: 32,
    [MD]: 24,
    [SM]: 20,
    [XS]: 16
  }
  return sizes[size] || 16
})

const TaskIcon = () => {
  if (props.objective.issueIcon) {
    return h('img', {
      src: props.objective.issueIcon,
      class: 'oi-IconTask',
      'data-issue-type': props.objective.issueType // hardcode solution coz we cant render this icons in roadmap image export because of CORS
    })
  } else {
    return h(AppIcon, {
      iconName: 'jira-objective',
      height: absoluteSize.value,
      width: absoluteSize.value
    })
  }
}

const WorkItemIcon = () => {
  const scopeId = getScopeId(getCurrentInstance())

  if (props.objective.issueIcon) {
    return h(
      'div',
      {
        class: 'oi-ObjectiveIcon',
        style: styles.value
      },
      h('img', {
        ...scopeId,
        src: props.objective.issueIcon,
        class: 'oi-WorkItemIcon'
      })
    )
  } else {
    return h(AppIcon, {
      iconName: 'work-item',
      height: absoluteSize.value,
      width: absoluteSize.value
    })
  }
}

const OkrElementIcon = () => {
  return h(
    'div',
    {
      class: 'oi-ObjectiveIcon',
      style: styles.value
    },
    h('span', props.objective.levelPrefix)
  )
}

const IconWrapper = (_, { slots, attrs }) => {
  return h(
    'div',
    {
      ...attrs,
      class: {
        'oi-OkrIcon': true,
        [`oi-OkrIcon-${props.size}`]: true,
        'oi-OkrIcon-filteredout': props.filteredOut
      }
    },
    h(slots.default)
  )
}
</script>

<style lang="scss" scoped>
/* this eslint plugin won't work with vue 3 render function in <script setup> */
/* eslint-disable vue-scoped-css/no-unused-selector */
.oi-OkrIcon {
  display: flex;
  position: relative;

  &-xs {
    width: 16px;
    height: 16px;
  }

  &-sm {
    width: 20px;
    height: 20px;
  }

  &-lg {
    width: 32px;
    height: 32px;
  }

  &-xl {
    width: 48px;
    height: 48px;
  }

  &-filteredout {
    opacity: 0.5;
  }
}

.oi-IconTask {
  width: 24px;
  height: 24px;

  .oi-OkrIcon-xs & {
    width: 16px;
    height: 16px;
  }

  .oi-OkrIcon-sm & {
    width: 20px;
    height: 20px;
  }

  .oi-OkrIcon-lg & {
    width: 32px;
    height: 32px;
  }
}

.oi-WorkItemIcon {
  width: 60%;
  height: 60%;
  filter: brightness(0) invert(1);
}

.oi-ObjectiveIcon {
  font-family: $system-ui;
  display: flex;
  width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
  border-radius: $border-radius-sm-next; //  $border-radius-sm;
  font-weight: fw('bold');
  font-size: $fs-9;
  line-height: 16px;
  color: $white;
  .is-safari & {
    white-space: nowrap;
  }

  .oi-OkrIcon-xs & {
    font-size: $fs-7;
    font-weight: fw('regular');
    width: 16px;
    height: 16px;
    border-radius: $border-radius-sm;
  }

  .oi-OkrIcon-sm & {
    font-size: $fs-10;
    font-weight: fw('regular');
    width: 20px;
    height: 20px;
  }

  .oi-OkrIcon-lg & {
    font-size: $fs-16;
    line-height: 1;
    font-weight: fw('bold');
    width: 32px;
    height: 32px;
  }

  .oi-OkrIcon-xl & {
    font-size: $fs-24;
    line-height: 1;
    font-weight: fw('bold');
    width: 48px;
    height: 48px;
    border-radius: $border-radius-md-next;
  }
}

.oi-LockIcon {
  position: absolute;
  left: 0;
  bottom: 0;
  background: $grey-2-next;
  box-shadow: $white 0 0 0 1px;
  display: flex;
  border-radius: 50%;
  z-index: 10;
}

// this styles are used in pdf or roadmap export dont remove it
.oi-ObjectiveIcon.fix-align {
  .oi-OkrIcon-lg & {
    font-size: $fs-18;
    font-weight: fw('semi-bold');
    line-height: 33px;
  }

  .oi-OkrIcon-md & {
    font-weight: fw('semi-bold');
    font-size: $fs-10;
    line-height: 23px;
  }

  .oi-OkrIcon-sm &,
  .oi-OkrIcon-xs & {
    font-size: $fs-7;
    line-height: 19px;
  }
}
</style>
